import React, { useEffect, useState, useMemo, useContext } from "react";
import { Modal, Checkbox, Input, Divider, Button } from "antd";
import _ from "lodash";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { ArrowRightOutlined } from "@ant-design/icons";
import { ThumbsUp, ThumbsDown, AlertTriangle } from "lucide-react";
import defaultCompanyLogo from "../../assets/images/default_company_logo.jpg";
import { getCompanyLogoUrl } from "../../utils/getCompanyLogoUrl";
import {
  getSubscriptionUserInfo,
  getUserInfo,
  getPartialPhoneNumber,
} from "../../utils/utils";
import { emailRegex } from "../../config";

import {
  submitErrorForm,
  postRelavantExecutive,
  getRelavantExecutive,
  resetPostRelavantExecutive,
  updateRelavantExecutive,
} from "../../actionCreator/executiveDetailsActionCreator";
import { AuthContext } from "../../context/AuthContext";
import { ReactComponent as Call } from "../../assets/svg/icons/call.svg";
import { ReactComponent as Location } from "../../assets/svg/icons/location-icon.svg";
import { ReactComponent as EmployeeSize } from "../../assets/svg/icons/employee-size.svg";
import { ReactComponent as WebLink } from "../../assets/svg/icons/web-link.svg";
import { ReactComponent as CompanyType } from "../../assets/svg/icons/company-type.svg";
import { ReactComponent as Revenue } from "../../assets/svg/icons/revenue.svg";
import noData from "../../assets/svg/icons/no-data.svg";

const RightPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [similarCount, setSimilarCount] = useState(5);
  const [checkExecutiveStatus, setCheckExecutiveStatus] = useState(0);
  const [openErrorForm, setOpenErrorForm] = useState(false);
  const formIntialValue = {
    telephone: { disabled: true, value: "", status: null },
    address: { disabled: true, value: "", status: null },
    city: { disabled: true, value: "", status: null },
    zip: { disabled: true, value: "", status: null },
    employee: { disabled: true, value: "", status: null },
    website: { disabled: true, value: "", status: null },
    name: { disabled: true, value: "", status: null },
    email: { disabled: true, value: "", status: null },
    comment: { disabled: true, value: "", status: null },
  };
  const { TextArea } = Input;
  const [errorForm, setErrorForm] = useState(formIntialValue);
  const [companyDetails, setCompanyDetails] = useState({});
  const executiveCompanyDetails = useSelector(
    (state) => state.executiveDetailsReducer.executiveCompanyDetails,
  );
  const similarExecutiveList = useSelector(
    (state) => state.executiveDetailsReducer.similarExecutiveList,
  );

  const executiveDetails = useSelector(
    (state) => state.executiveDetailsReducer.executiveDetails,
  );

  const getRelavantExecutiveDetails = useSelector(
    (state) => state.executiveDetailsReducer.getRelavantExecutive,
  );

  useEffect(() => {
    if (!executiveCompanyDetails.length) return;
    const [data] = executiveCompanyDetails;
    setCompanyDetails(data);
  }, [executiveCompanyDetails]);

  useMemo(() => {
    dispatch(resetPostRelavantExecutive);
    if (Object.keys(getUserInfo()).length) {
      const { id } = getUserInfo();
      dispatch(getRelavantExecutive(id, executiveDetails?.id));
    }
  }, [dispatch, executiveDetails]);

  useEffect(() => {
    const [data] = getRelavantExecutiveDetails || [];
    if (data) {
      setCheckExecutiveStatus(data?.prescribedby === "True" ? 1 : 2);
    }
  }, [getRelavantExecutiveDetails]);

  useEffect(() => {
    setSimilarCount(
      similarExecutiveList.length < 5 ? similarExecutiveList.length : 5,
    );
  }, [similarExecutiveList]);

  const renderSimilarExecutiveList = () => {
    let similarList = [];
    const count =
      similarExecutiveList?.length > similarCount
        ? similarCount
        : similarExecutiveList?.length;
    for (let i = 0; i < count; i++) {
      const exec = similarExecutiveList[i];
      if (!exec) continue;
      const initials = `${exec?.firstname?.[0] || ""}${exec?.lastname?.[0] || ""}`.toUpperCase();
      similarList.push(
        <div
          key={`similarExec_${i}`}
          className="tw-flex tw-group tw-gap-4 tw-p-3 hover:tw-bg-slate-50/50 tw-border tw-border-solid tw-border-transparent hover:tw-border-slate-100 tw-rounded-xl tw-transition-all tw-cursor-pointer tw-mb-2 last:tw-mb-0"
          onClick={() => {
            navigate(`/executive-details/${exec?.id}/${exec?.fullname?.replaceAll(" ", "-")}`);
          }}
        >
          <div className="tw-bg-indigo-50 tw-text-theme tw-font-bold tw-size-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-text-sm tw-shrink-0 tw-shadow-sm">
            {initials || "?"}
          </div>
          <div className="tw-flex tw-flex-col tw-w-full tw-gap-1 tw-min-w-0">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <span
                className="tw-font-bold tw-text-sm tw-text-slate-800 hover:tw-text-theme tw-truncate tw-transition-colors tw-min-w-0 tw-flex-1"
                title={exec?.fullname || `${exec?.firstname} ${exec?.lastname}`}
              >
                {exec?.fullname || `${exec?.firstname} ${exec?.lastname}`}
              </span>
              <ArrowRightOutlined className="tw-text-xs tw-text-slate-400 group-hover:tw-text-theme tw-transition-colors" />
            </div>
            <div className="tw-text-xs tw-font-medium tw-text-slate-400 tw-truncate">
              {exec?.title}
            </div>
          </div>
        </div>,
      );
    }
    if (!similarList.length) {
      similarList.push(
        <div className="tw-flex tw-items-center tw-justify-center tw-my-6" key="no-data">
          <img src={noData} alt="" />
        </div>,
      );
    }
    return similarList;
  };

  const checkRelavantExecutive = (flag, thumbStatus) => {
    //checkRelavantExecutive
    const isLoggedIn = isAuthenticated;
    if (isLoggedIn) {
      const [data] = getRelavantExecutiveDetails || [];

      const { id } = getUserInfo();
      const { id: accountId } = getSubscriptionUserInfo();
      if (data?.id) {
        //update case
        let payload = {
          id: data?.id,
          accountId: accountId,
          emplaoyeeId: executiveDetails?.id,
          iscompany: false,
          prescribedby: flag ? "True" : "False",
          userId: id,
        };
        dispatch(updateRelavantExecutive(payload));
      } else {
        let payload = {
          accountId: accountId,
          emplaoyeeId: executiveDetails?.id,
          iscompany: false,
          prescribedby: flag ? "True" : "False",
          userId: id,
        };
        dispatch(postRelavantExecutive(payload));
      }
    }
  };

  const handleErrorForm = () => {
    setOpenErrorForm(true);
  };

  const closeErrorForm = () => {
    setOpenErrorForm(false);
    setErrorForm(formIntialValue);
  };

  const enableField = (ele) => {
    setErrorForm({
      ...errorForm,
      [ele.target.name]: ele.target.checked
        ? { ...errorForm[ele.target.name], disabled: !ele.target.checked }
        : { value: "", disabled: !ele.target.checked },
    });
  };

  const onSubmitForm = () => {
    const regEx = new RegExp(emailRegex);
    const clone = _.cloneDeep(errorForm);
    if (!errorForm?.name?.value) {
      clone.name.status = "error";
      setErrorForm(clone);
      return false;
    }

    if (!errorForm?.email?.value || !regEx.test(errorForm?.email?.value)) {
      clone.email.status = "error";
      setErrorForm(clone);
      return false;
    }

    let newPayload = {};
    Object.keys(errorForm).forEach((key) => {
      newPayload = {
        ...newPayload,
        [key]: errorForm[key].value,
      };
    });
    const { id } = getUserInfo();
    const { id: accountId } = getSubscriptionUserInfo();
    const payload = {
      accountId: accountId,
      emplaoyeeId: executiveDetails?.id,
      description: JSON.stringify(newPayload),
      iscompany: false,
      iscorrected: true,
      userId: id,
    };
    // errorForm
    dispatch(submitErrorForm(payload));
    setOpenErrorForm(false);
    setErrorForm(formIntialValue);
  };

  const onInputChange = (ele) => {
    setErrorForm({
      ...errorForm,
      [ele.target.name]: {
        ...errorForm[ele.target.name],
        value: ele.target.value,
        status: null,
      },
    });
  };
  const getDetails = (id, name) => {
    let cname = name.replaceAll(" ", "-", name);
    navigate(`/company-summary/${id}/${cname}`);
  };
  return (
    <>
      <div className="tw-font-inter">
        {/* Company Information Card */}
        <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-5 tw-mb-5">
          <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-pb-4 tw-border-b tw-border-solid tw-border-slate-100 tw-flex tw-items-center tw-gap-2.5 tw-m-0">
            <CompanyType className="tw-size-4.5 tw-text-slate-500" />
            Company Information
          </h3>
          <div className="tw-bg-indigo-50/30 tw-rounded-xl tw-border tw-border-solid tw-border-indigo-100/50 tw-p-3.5 tw-flex tw-items-center tw-gap-3.5 tw-mt-4 tw-transition-all hover:tw-shadow-sm">
            <div className="tw-bg-slate-50 tw-size-12 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/60 tw-overflow-hidden tw-shrink-0">
              {companyDetails?.companyLogoUrl ? (
                <img
                  src={getCompanyLogoUrl(companyDetails?.companyLogoUrl)}
                  alt="Company logo"
                  className="tw-w-full tw-h-full tw-object-contain"
                  onError={(e) => {
                    e.currentTarget.src = defaultCompanyLogo;
                  }}
                />
              ) : (
                <img
                  src={defaultCompanyLogo}
                  alt="Company logo"
                  className="tw-w-full tw-h-full tw-object-contain"
                />
              )}
            </div>

            <div className="tw-min-w-0">
              <div className="tw-text-[10px] tw-text-slate-450 tw-font-bold tw-uppercase tw-tracking-wider tw-mb-0.5">
                Company
              </div>
              <span
                className="tw-text-sm tw-font-bold tw-text-slate-700 hover:tw-text-theme tw-cursor-pointer tw-block tw-break-words"
                onClick={() =>
                  getDetails(
                    `${executiveDetails?.company?.id}`,
                    `${companyDetails?.name || executiveDetails?.company?.name}`,
                  )
                }
              >
                {companyDetails?.name || executiveDetails?.company?.name}
              </span>
              {!!companyDetails?.industry?.name && (
                <span className="tw-text-xs tw-font-medium tw-text-slate-500 tw-block tw-mt-0.5">
                  {companyDetails?.industry?.name}
                </span>
              )}
            </div>
          </div>


          <div className="tw-flex tw-flex-col tw-gap-1 tw-text-slate-600 tw-mt-4 tw-pt-1">
            {companyDetails?.phoneNo && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                <Call className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-break-all">
                  {getPartialPhoneNumber(companyDetails?.phoneNo)}
                </span>
              </div>
            )}

            {companyDetails?.range?.name && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                <EmployeeSize className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                <span className="tw-text-xs tw-font-semibold tw-text-slate-700">
                  {companyDetails?.range?.name}
                </span>
              </div>
            )}

            {companyDetails?.wedsite && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                <WebLink className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                <span
                  className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-break-all tw-underline hover:tw-text-theme tw-transition-colors tw-min-w-0 tw-flex-1 tw-cursor-pointer"
                  onClick={() =>
                    window.open(
                      companyDetails?.wedsite.startsWith("http")
                        ? companyDetails.wedsite
                        : `http://${companyDetails.wedsite}`,
                      "_blank",
                    )
                  }
                >
                  {companyDetails?.wedsite}
                </span>
              </div>
            )}

            {companyDetails?.category?.name && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                <CompanyType className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-break-words">
                  {companyDetails?.category?.name}
                </span>
              </div>
            )}

            {companyDetails?.revenue?.name && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                <Revenue className="tw-size-4 tw-text-slate-400 tw-shrink-0" />
                <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-break-words">
                  {companyDetails?.revenue?.name}
                </span>
              </div>
            )}

            {companyDetails?.address && (
              <div className="tw-flex tw-items-start tw-gap-3 tw-py-1.5 tw-px-2 tw-rounded-xl hover:tw-bg-slate-50 tw-transition-colors">
                <Location className="tw-size-4 tw-text-slate-400 tw-shrink-0 tw-mt-0.5" />
                <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-leading-relaxed tw-break-words tw-whitespace-normal tw-min-w-0 tw-flex-1">
                  {companyDetails?.address}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Similar Executive Card */}
        <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-5 tw-mb-5">
          <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-pb-4 tw-border-b tw-border-solid tw-border-slate-100 tw-flex tw-items-center tw-gap-2.5 tw-m-0">
            <EmployeeSize className="tw-size-4.5 tw-text-slate-500" />
            Similar Executive
          </h3>

          <div className="tw-mt-4">{renderSimilarExecutiveList()}</div>
        </div>

        {/* Feedback Section */}
        <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-shadow-sm tw-p-5 tw-mt-5 tw-text-sm tw-flex tw-flex-col tw-items-center tw-justify-center">
          <span className="tw-font-bold tw-text-slate-700 tw-mb-3">Is this executive data relevant to you?</span>
          <div className="tw-flex tw-gap-4">
            {checkExecutiveStatus === 1 ? (
              <span className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-indigo-50 tw-text-theme tw-cursor-pointer tw-shadow-sm">
                <ThumbsUp size={18} />
              </span>
            ) : (
              <span
                onClick={() => checkRelavantExecutive(1, checkExecutiveStatus)}
                className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-slate-50 tw-text-slate-450 hover:tw-bg-indigo-50 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border tw-border-solid tw-border-slate-200/60"
              >
                <ThumbsUp size={18} />
              </span>
            )}
            {checkExecutiveStatus === 2 ? (
              <span className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-rose-50 tw-text-[#f44336] tw-cursor-pointer tw-shadow-sm">
                <ThumbsDown size={18} />
              </span>
            ) : (
              <span
                onClick={() => checkRelavantExecutive(0, checkExecutiveStatus)}
                className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-slate-50 tw-text-slate-450 hover:tw-bg-rose-50 hover:tw-text-[#f44336] tw-cursor-pointer tw-transition-all tw-border tw-border-solid tw-border-slate-200/60"
              >
                <ThumbsDown size={18} />
              </span>
            )}
          </div>
        </div>

        {/* Report An Error Button */}
        <div
          className="tw-font-bold tw-py-2.5 tw-px-4 tw-gap-2 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-cursor-pointer tw-text-sm tw-mt-4 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-rose-300 hover:tw-bg-rose-50/10 hover:tw-text-rose-600 tw-text-slate-650 tw-transition-all tw-shadow-sm"
          onClick={handleErrorForm}
        >
          <AlertTriangle size={16} className="tw-text-amber-500" />
          Report An Error
        </div>
      </div>
      {openErrorForm && (
        <Modal
          title={
            <div className="tw-flex tw-flex-col tw-gap-0.5">
              <span className="tw-text-xs tw-font-bold tw-text-theme tw-uppercase tw-tracking-wider">
                Report an Error
              </span>
              <span className="tw-text-base tw-font-bold tw-text-slate-800">
                Report Incorrect Information
              </span>
            </div>
          }
          width="480px"
          closable
          centered
          open={openErrorForm}
          onCancel={closeErrorForm}
          footer={[
            <Button
              key="cancel"
              onClick={closeErrorForm}
              className="tw-border-slate-200 hover:tw-border-slate-300 tw-text-slate-600 hover:tw-text-slate-800 tw-text-xs tw-font-bold tw-h-10 tw-rounded-xl tw-px-5"
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={onSubmitForm}
              className="!tw-bg-theme hover:!tw-bg-theme/90 !tw-border-theme tw-text-white tw-text-xs tw-font-bold tw-h-10 tw-rounded-xl tw-px-6"
            >
              Submit
            </Button>,
          ]}
        >
          <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-3.5 tw-font-inter">
            <p className="tw-text-xs tw-font-medium tw-text-slate-500 tw-leading-relaxed tw-mb-1">
              Please select the appropriate checkbox that you have found
              Incorrect and if you know the correct data please provide us in
              appropriate text box.
            </p>

            {/* Telephone */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="telephone" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Telephone
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="telephone"
                  value={errorForm?.telephone?.value}
                  placeholder="Telephone"
                  disabled={errorForm?.telephone?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Address */}
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0 tw-mt-2">
                <Checkbox name="address" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Address
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <TextArea
                  name="address"
                  value={errorForm?.address?.value}
                  rows={2}
                  maxLength={100}
                  disabled={errorForm?.address?.disabled}
                  onChange={onInputChange}
                  placeholder="Address"
                  className="tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-p-2.5"
                />
              </div>
            </div>

            {/* City */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="city" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  City
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="city"
                  value={errorForm?.city?.value}
                  placeholder="City"
                  disabled={errorForm?.city?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Zip */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="zip" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Zip/Pin code
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="zip"
                  value={errorForm?.zip?.value}
                  placeholder="Zip/Pin code"
                  disabled={errorForm?.zip?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Employee */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="employee" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  No. of Employees
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="employee"
                  value={errorForm?.employee?.value}
                  placeholder="No. of Employees"
                  disabled={errorForm?.employee?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Website */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="website" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Website
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="website"
                  value={errorForm?.website?.value}
                  placeholder="Website"
                  disabled={errorForm?.website?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            <Divider dashed={true} className="tw-my-1" />

            {/* Name */}
            <div>
              <Input
                name="name"
                status={errorForm?.name?.status}
                value={errorForm?.name?.value}
                placeholder="Your Name*"
                onChange={onInputChange}
                className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme tw-px-3"
              />
            </div>

            {/* Email */}
            <div>
              <Input
                name="email"
                status={errorForm?.email?.status}
                value={errorForm?.email?.value}
                placeholder="Your Email ID*"
                onChange={onInputChange}
                className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme tw-px-3"
              />
            </div>

            {/* Comment */}
            <div>
              <TextArea
                name="comment"
                value={errorForm?.comment?.value}
                rows={3}
                maxLength={100}
                placeholder="Comment if any"
                onChange={onInputChange}
                className="tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme tw-p-2.5"
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default RightPanel;
